/**
 * @file invoiceHtml.ts
 * @brief Reads the invoice HTML into fields for the in-app invoice menu
 *        (`InvoiceMenu`); the paper layout is only the downloadable PDF.
 *
 * @details
 * DES 04-10-2026: the sheet used to iframe the printable white "paper"
 * invoice. The preview is now a menu shaped like the session "Paid" cost
 * menu, with action buttons (DES 04-10-2026); the paper layout lives only in
 * the PDF.
 *
 * Data comes from the SAME `GET /api/invoices/:id/html` render the PDF is
 * built from, read with DOMParser. Billing is trust territory: re-deriving
 * the amounts client-side could drift from the PDF, reading the server's own
 * strings cannot. It needs no backend change. If the markup ever stops
 * parsing, `parseInvoiceHtml` returns null and the menu says so and still
 * offers the PDF, so it never shows an empty invoice.
 *
 * @references
 * - BACKEND/src/features/invoices/routes.js (GET /:id/html markup)
 * - components/feedback/InvoiceMenu.tsx (caller)
 *
 * @author DES
 * @date 04-10-2026
 */


export interface ParsedInvoice {
  from: string[];
  billTo: string[];
  details: Array<[string, string]>;
  amounts: Array<[string, string]>;
  /** The bold "Amount charged" row. */
  total: [string, string] | null;
  generated: string | null;
}

const text = (el: Element | null | undefined) =>
  (el?.textContent ?? "").replace(/\s+/g, " ").trim();

function rows(table: Element | null): Array<[string, string]> {
  if (!table) return [];
  return Array.from(table.querySelectorAll("tr"))
    .map((tr) => {
      const tds = tr.querySelectorAll("td");
      return [text(tds[0]), text(tds[1])] as [string, string];
    })
    .filter(([k]) => k.length > 0);
}

function partyLines(party: Element | undefined): string[] {
  if (!party) return [];
  return Array.from(party.querySelectorAll("p:not(.label)"))
    .map((p) => text(p))
    .filter((s) => s && s !== "—");
}

/** Returns null when the markup is not the invoice shape we know. */
export function parseInvoiceHtml(html: string): ParsedInvoice | null {
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const amountsTable = doc.querySelector("table.amounts");
    const amountRows = Array.from(amountsTable?.querySelectorAll("tr") ?? []);
    if (amountRows.length === 0) return null;

    const totalTr = amountRows.find((tr) => tr.classList.contains("total"));
    const total = totalTr
      ? ([
          text(totalTr.querySelectorAll("td")[0]),
          text(totalTr.querySelectorAll("td")[1]),
        ] as [string, string])
      : null;
    const amounts = amountRows
      .filter((tr) => tr !== totalTr)
      .map(
        (tr) =>
          [
            text(tr.querySelectorAll("td")[0]),
            text(tr.querySelectorAll("td")[1]),
          ] as [string, string],
      );

    const parties = Array.from(doc.querySelectorAll(".party"));
    return {
      from: partyLines(parties[0]),
      billTo: partyLines(parties[1]),
      details: rows(doc.querySelector("table.details")),
      amounts,
      total,
      generated: text(doc.querySelector(".footer")) || null,
    };
  } catch {
    return null;
  }
}

/** "2.00 GBP" → ["£", "2.00"] for the brand-red symbol (Paid menu style). */
export function splitMoney(raw: string): [string, string] {
  const m = raw.match(/^(-?[\d.,]+)\s+([A-Z]{3})$/);
  if (!m) return ["", raw];
  try {
    const parts = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: m[2],
    }).formatToParts(Number(m[1].replace(/,/g, "")));
    const symbol = parts
      .filter((p) => p.type === "currency")
      .map((p) => p.value)
      .join("");
    const amount = parts
      .filter((p) => p.type !== "currency" && p.type !== "literal")
      .map((p) => p.value)
      .join("");
    return [symbol, amount];
  } catch {
    return ["", raw];
  }
}

/** "2.00 GBP" → "£2.00". Unknown shapes pass through untouched. */
export function formatMoney(raw: string): string {
  const m = raw.match(/^(-?[\d.,]+)\s+([A-Z]{3})$/);
  if (!m) return raw;
  const value = Number(m[1].replace(/,/g, ""));
  if (!Number.isFinite(value)) return raw;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: m[2],
    }).format(value);
  } catch {
    return raw;
  }
}

/** "04/10/2026, 10:04:37" → "04/10/2026, 10:04" (server's own locale string). */
const dropSeconds = (v: string) => v.replace(/(\d{1,2}:\d{2}):\d{2}/, "$1");

/** "Started – Ended" on one line; the date is not repeated when it matches. */
export function timeRange(started?: string, ended?: string): string | null {
  if (!started || started === "-") return null;
  const a = dropSeconds(started);
  if (!ended || ended === "-") return a;
  const b = dropSeconds(ended);
  const [aDate] = a.split(", ");
  const [bDate, bTime] = b.split(", ");
  return aDate === bDate && bTime ? `${a} – ${bTime}` : `${a} – ${b}`;
}

